import Color from 'color';

export const getLightness = (x: number) => {
  return 0.0012 * (x * x) - 0.1495 * x + 5.1;
};

export namespace ColorUtil {
  type Shade = 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950;

  export const shades: Shade[] = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];

  function lightnessByShade(shade: Shade) {
    return (1000 - shade) / 10;
  }

  export function getShadedColor(color: string, shade: Shade) {
    return new Color(color).lightness(lightnessByShade(shade)).hex();
  }

  export function isDarkColor(color: string) {
    return new Color(color).isDark();
  }

  export function isLightColor(color: string) {
    return new Color(color).isLight();
  }

  export function setPrimaryColor(document: Document, primaryColor: string) {
    document.documentElement.style.setProperty('--primary-color', primaryColor);
    for (const shade of shades) {
      document.documentElement.style.setProperty(`--primary-color-${shade}`, getShadedColor(primaryColor, shade));
    }

    if (new Color(primaryColor).isLight()) {
      document.documentElement.style.setProperty('--on-primary-color', '#181D27'); // Gray 900
    } else {
      document.documentElement.style.setProperty('--on-primary-color', '#F5F5F5'); // Gray 100
    }
  }

  export function setHeaderColor(document: Document, headerColor: string) {
    document.documentElement.style.setProperty('--header-color', headerColor);

    if (new Color(headerColor).isLight()) {
      document.documentElement.style.setProperty('--on-header-color', '#181D27'); // Gray 900
    } else {
      document.documentElement.style.setProperty('--on-header-color', '#F5F5F5'); // Gray 100
    }
  }

  export function setAccentColor(document: Document, accentColor: string) {
    document.documentElement.style.setProperty('--accent-color', accentColor);
  }

  export function setCompanyColor(
    document: Document,
    colors: { accentColor?: string; primaryColor: string; headerColor: string },
  ) {
    const { accentColor, primaryColor, headerColor } = colors;

    if (accentColor) setAccentColor(document, accentColor);
    setPrimaryColor(document, primaryColor);
    setHeaderColor(document, headerColor);
  }

  interface ShadeOptions {
    range?: number;
    order?: 'dark-to-light' | 'light-to-dark' | 'around';
  }

  export function generateShades(baseHex: string, count: number, opts: ShadeOptions = {}): string[] {
    if (count < 1) return [];
    const range = clamp(opts.range ?? 0.4, 0, 1);
    const order = opts.order ?? 'dark-to-light';

    const { h, s, l } = hexToHsl(baseHex);

    const others = Math.max(count - 1, 0);
    const darkerCount = Math.floor(others / 2);
    const lighterCount = others - darkerCount;

    const maxDark = Math.min(l, range);
    const maxLight = Math.min(1 - l, range);
    const darkStep = darkerCount > 0 ? maxDark / darkerCount : 0;
    const lightStep = lighterCount > 0 ? maxLight / lighterCount : 0;

    const darker: string[] = [];
    for (let i = darkerCount; i >= 1; i--) {
      darker.push(hslToHex(h, s, clamp(l - i * darkStep, 0, 1)));
    }

    const base = hslToHex(h, s, l);

    const lighter: string[] = [];
    for (let i = 1; i <= lighterCount; i++) {
      lighter.push(hslToHex(h, s, clamp(l + i * lightStep, 0, 1)));
    }

    const around = [...darker, base, ...lighter];

    if (order === 'around') return around;
    if (order === 'light-to-dark') return [...lighter].reverse().concat([base], [...darker].reverse());
    return [...darker, base, ...lighter];
  }

  // ---- Helpers ----

  function clamp(n: number, min: number, max: number) {
    return Math.min(max, Math.max(min, n));
  }

  function hexToRgb(hex: string): { r: number; g: number; b: number } {
    let h = hex.replace('#', '');
    if (h.length === 3)
      h = h
        .split('')
        .map(c => c + c)
        .join('');
    const int = parseInt(h, 16);
    return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
  }

  function rgbToHex(r: number, g: number, b: number): string {
    const toHex = (v: number) => v.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  function hexToHsl(hex: string): { h: number; s: number; l: number } {
    const { r, g, b } = hexToRgb(hex);
    const rN = r / 255,
      gN = g / 255,
      bN = b / 255;

    const max = Math.max(rN, gN, bN),
      min = Math.min(rN, gN, bN);
    const delta = max - min;

    let h = 0;
    if (delta !== 0) {
      if (max === rN) h = ((gN - bN) / delta) % 6;
      else if (max === gN) h = (bN - rN) / delta + 2;
      else h = (rN - gN) / delta + 4;
      h *= 60;
      if (h < 0) h += 360;
    }

    const l = (max + min) / 2;
    const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
    return { h, s, l };
  }

  function hslToHex(h: number, s: number, l: number): string {
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;

    let rP = 0,
      gP = 0,
      bP = 0;
    if (0 <= h && h < 60) [rP, gP, bP] = [c, x, 0];
    else if (60 <= h && h < 120) [rP, gP, bP] = [x, c, 0];
    else if (120 <= h && h < 180) [rP, gP, bP] = [0, c, x];
    else if (180 <= h && h < 240) [rP, gP, bP] = [0, x, c];
    else if (240 <= h && h < 300) [rP, gP, bP] = [x, 0, c];
    else [rP, gP, bP] = [c, 0, x];

    const r = Math.round((rP + m) * 255);
    const g = Math.round((gP + m) * 255);
    const b = Math.round((bP + m) * 255);
    return rgbToHex(r, g, b);
  }
}
